import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import { findPhaseScheduleItem, getDirectionOfMovingItem } from '../functions';
import { changeItemsForMoving, changePercentageItem, processOrderingItemsAndSetNumbersWBS } from '../gantChangeData';
import { getMappingIdHideCollapse } from '../gantTransformData';
import useCheckCycleLink from '../hooks/useCheckCycleLink';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantDraggable } from '../store/slices/gantDraggableSlice';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setMappingItemIdIndex, setterGant } from '../store/slices/gantSlice';
import { DirectionDragItemType, DragAndDropType, ItemType, MappingItemIdIndexType } from '../types';

const LONG_PRESS_DELAY = 500;

let isDraggingItemBox = false;

const useControlLeftHeader = () => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const environment = useRelayEnvironment();
    const currentItemVerticalDraggableStore = useSelector<RootState, ItemType | null>(
        (state) => state.gantDraggable.currentItemVerticalDraggable,
    );
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);

    const typeOfDragAndDropStore = useSelector<RootState, DragAndDropType>(
        (state) => state.gantDraggable.typeOfDragAndDrop,
    );
    const currentItemVerticalDropStore = useSelector<RootState, ItemType | null>(
        (state) => state.gantDraggable.currentItemVerticalDrop,
    );
    const checkCycleLink = useCheckCycleLink();

    const widthColumn = useSelector<RootState, number>((state) => state.gant.widthColumn);

    const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);

    const updateParentChildrenLink = (dragItemId: string, dropItemId: string) => {
        let updatedItems = [...itemsList];

        const dragItem = updatedItems[mappingItemIdIndex[dragItemId]];

        // remove item id from previous parent item
        const previousParentItem = updatedItems[mappingItemIdIndex[dragItem.parentId!]];
        updatedItems[mappingItemIdIndex[previousParentItem.id]] = {
            ...previousParentItem,
            children: previousParentItem.children?.filter((childId) => childId !== dragItemId),
        };

        const dropItem = updatedItems[mappingItemIdIndex[dropItemId]];
        const [firstDropItemChild] = dropItem.children || [];

        // change previous parent id to new parent id
        updatedItems[mappingItemIdIndex[dragItemId]] = {
            ...dragItem,
            parentId: dropItemId,
            phaseId: findPhaseScheduleItem(dropItemId, environment) || undefined,
            hideForCollapse: updatedItems[mappingItemIdIndex[firstDropItemChild]]?.hideForCollapse || false,
        };

        // add item to children for parent
        updatedItems[mappingItemIdIndex[dropItemId]] = {
            ...dropItem,
            children: [dragItemId, ...(dropItem.children || [])],
        };

        const listItemForUpdate = [
            updatedItems[mappingItemIdIndex[previousParentItem.id]],
            updatedItems[mappingItemIdIndex[dragItemId]],
        ];

        listItemForUpdate.forEach((item) => {
            updatedItems = changeItemsForMoving(mappingItemIdIndex, 0, 'both', item, updatedItems, []);
        });

        updatedItems = changePercentageItem(
            mappingItemIdIndex,
            mappingItemIdIndex[dragItemId],
            updatedItems[mappingItemIdIndex[dragItemId]],
            updatedItems,
            widthColumn,
        );

        const { mappingScheduleItemIdIndex, newItems } = processOrderingItemsAndSetNumbersWBS(updatedItems);
        const newMappingItemIdHideCollapse = getMappingIdHideCollapse(newItems);

        setTimeout(() => {
            dispatch(
                setterGantItems({
                    itemsList: newItems,
                }),
            );

            dispatch(
                setterGant({
                    // countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
                    mappingItemIdHideCollapse: newMappingItemIdHideCollapse,
                }),
            );
            dispatch(setMappingItemIdIndex(mappingScheduleItemIdIndex));
        });
    };

    const findDropItem = (targetParentId: string, dropItem: ItemType): ItemType => {
        if (targetParentId === dropItem.parentId) {
            return dropItem;
        } else {
            return findDropItem(targetParentId, itemsList[mappingItemIdIndex[dropItem.parentId!]]);
        }
    };

    const changeOrderingItems = (
        directionOfMovingItem: DirectionDragItemType,
        dragItemId: string,
        dropItemId: string,
    ) => {
        const dragItem = itemsList[mappingItemIdIndex[dragItemId]];

        const dropItem = itemsList[mappingItemIdIndex[dropItemId]];
        const newDropItem = findDropItem(dragItem.parentId!, dropItem);

        const dropParentItem = { ...itemsList[mappingItemIdIndex[newDropItem.parentId!]] };

        dropParentItem.children = dropParentItem.children?.filter((childId) => childId !== dragItemId);
        const index = dropParentItem.children!.indexOf(newDropItem.id);

        const delta = directionOfMovingItem === 'down' ? 1 : 0;
        dropParentItem.children!.splice(index + delta, 0, dragItemId);

        const copyItems = [...itemsList];
        copyItems[mappingItemIdIndex[dropParentItem.id]] = { ...dropParentItem };

        const { mappingScheduleItemIdIndex, newItems } = processOrderingItemsAndSetNumbersWBS(copyItems);

        dispatch(
            setterGantItems({
                itemsList: newItems,
            }),
        );

        dispatch(
            setterGant({
                countVisibleRow: newItems.filter((item) => !item.hideForCollapse).length,
                mappingItemIdIndex: mappingScheduleItemIdIndex,
            }),
        );
    };

    const finishDragging = (currentItem?: ItemType, dropItem?: ItemType, dragType?: DragAndDropType) => {
        const currentItemVerticalDrop = dropItem ?? currentItemVerticalDropStore;
        const currentItemVerticalDraggable = currentItem ?? currentItemVerticalDraggableStore;
        const typeOfDragAndDrop = dragType ?? typeOfDragAndDropStore;

        if (
            currentItemVerticalDraggable &&
            currentItemVerticalDrop &&
            currentItemVerticalDraggable.id !== currentItemVerticalDrop.id
        ) {
            if (typeOfDragAndDrop === 'parent') {
                if (!checkCycleLink(currentItemVerticalDraggable, currentItemVerticalDrop)) {
                    return;
                }

                updateParentChildrenLink(currentItemVerticalDraggable.id, currentItemVerticalDrop.id);
            } else {
                const directionOfMovingItem = getDirectionOfMovingItem(
                    currentItemVerticalDraggable,
                    currentItemVerticalDrop,
                );

                if (directionOfMovingItem !== 'none') {
                    changeOrderingItems(
                        directionOfMovingItem,
                        currentItemVerticalDraggable.id,
                        currentItemVerticalDrop.id,
                    );
                }
            }
        }
        dispatch(
            setterGantDraggable({
                currentItemVerticalDraggable: null,
                currentItemVerticalDrop: null,
                typeOfDragAndDrop: 'none',
            }),
        );
    };

    const startDragging = (item: ItemType, typeOfDragAndDrop?: DragAndDropType) => {
        if (item.roleItem === 'project') {
            return;
        }

        if (typeOfDragAndDrop) {
            dispatch(
                setterGantDraggable({
                    currentItemVerticalDraggable: item,
                    typeOfDragAndDrop,
                }),
            );
        } else {
            dispatch(
                setterGantDraggable({
                    currentItemVerticalDraggable: item,
                }),
            );
        }

        dispatch(setterGantItems({ currentGantItem: null, isItemEdit: false }));
    };

    const handleMouseUp = (item?: ItemType) => () => {
        if (pressTimer) {
            clearTimeout(pressTimer);
            setPressTimer(null);
        }

        if (isDraggingItemBox) {
            finishDragging(item);
            isDraggingItemBox = false;
        } else {
            dispatch(
                setterGantItems({
                    isItemEdit: true,
                    currentGantItem: item,
                }),
            );
        }
    };

    const handleMouseDown =
        (item: ItemType, typeOfDragAndDrop: DragAndDropType = 'none') =>
        (e: React.MouseEvent<SVGElement>) => {
            setPressTimer(
                setTimeout(() => {
                    e.preventDefault();
                    isDraggingItemBox = true;
                    startDragging(item, typeOfDragAndDrop);
                }, LONG_PRESS_DELAY),
            );
        };

    return {
        finishDragging,
        startDragging,
        handleMouseUp,
        handleMouseDown,
        updateParentChildrenLink,
    };
};

export default useControlLeftHeader;
